import { defineMessages, FormattedMessage } from 'react-intl-next';
import React, { useContext, useEffect } from 'react';

import InfoIcon from '@atlaskit/icon/core/information';
import { Inline, xcss, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { CoreInvitesContext, CoreInvitesSource } from '@confluence/core-invites-provider';
import { Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../controller/Controller';
import { PressableLink } from '../../../PressableLink';
import type { UserPickerEmailPrincipal } from '../../commonTypes';

const i18n = defineMessages({
	inviteMessage: {
		id: 'share-and-restrict-dialog.button.restriction.message.invite.text',
		defaultMessage:
			'Invite <b>{userEmail} {userCount, plural, =0 {} one {and # other} other {and # others}}</b> to join Confluence? Otherwise, we’ll just send a notification when you select Share.',
		description: 'Tells the user there might be restrictions on this content before sharing',
	},
	invitePeopleCta: {
		id: 'share-and-restrict-dialog.button.restriction.invite.action.text',
		defaultMessage: 'Invite them',
		description: 'Action for user to change the restrictions on this content being shared',
	},
});

type InviteMessageProps = {
	userEmails: UserPickerEmailPrincipal[] | undefined | null;
};

const inviteMessageContainer = xcss({
	padding: 'space.200',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
});

const Bold = (chunks: any) => <Text weight="bold">{chunks}</Text>;

export const InviteMessageComponent = ({ userEmails }: InviteMessageProps) => {
	const { openCoreInvites } = useContext(CoreInvitesContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (!openCoreInvites || !userEmails || userEmails?.length === 0) return;

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'informInviteOnShare',
				attributes: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, openCoreInvites, userEmails]);

	if (!openCoreInvites || !userEmails || userEmails?.length === 0) return null;

	const handleCtaClick = () => {
		openCoreInvites(
			CoreInvitesSource.SHARE_AND_RESTRICT_DIALOG,
			undefined,
			userEmails.map(({ id, name, type }) => ({
				email: id,
				name,
				type,
				id,
			})),
		);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'invitePeople',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				attributes: {
					emailCount: userEmails?.length,
				},
			},
		}).fire();
	};

	return (
		<Inline alignBlock="start" space="space.200" xcss={inviteMessageContainer}>
			<InfoIcon label="" color={token('color.icon.subtle')} />
			<Stack alignInline="start" space="space.100">
				<Text align="start" color="color.text.subtle" size="medium">
					<FormattedMessage
						{...i18n.inviteMessage}
						values={{
							userEmail: userEmails[0]?.id,
							userCount: userEmails?.length - 1,
							b: Bold,
						}}
					/>
				</Text>
				<Text color="color.link" weight="semibold" size="medium">
					<PressableLink isBlue onClick={handleCtaClick}>
						<FormattedMessage {...i18n.invitePeopleCta} />
					</PressableLink>
				</Text>
			</Stack>
		</Inline>
	);
};

export const InviteMessage = (props: InviteMessageProps) => (
	<TransparentErrorBoundary attribution={Attribution.CC_ONBOARDING}>
		<InviteMessageComponent {...props} />
	</TransparentErrorBoundary>
);
